<div class="new-note">
  <form class="new-note__form">
    <!-- Selections -->
    <div class="new-note__selection-section">
      <v-one-input
        [formControl]="conceptControl"
        [type]="'select'"
        [options]="conceptOptions()"
        [placeholder]="selectedConcept()"
        [label]="'global.notes.select-concept' | translate"
      />

      <div class="new-note__selection-divider"></div>

      <v-one-input
        [formControl]="typeControl"
        [type]="'select'"
        [options]="typeOptions()"
        [placeholder]="'global.notes.by-article' | translate"
        [label]="'global.notes.select-note-type' | translate"
        [required]="true"
      />
    </div>

    <!-- MOBILE -->
    <div class="new-note__details-section new-note__details-section--mobile">
      @if (selectedArticleType() === NOTE_ARTICLE_TYPE.BY_ARTICLE) {
        <div class="new-note__search-wrapper">
          <v-one-search
            [placeholder]="'global.notes.search' | translate"
            (searchChange)="onSearchChange($event)"
          />
        </div>
      }

      <!-- IVA Checkbox -->
      <div class="new-note__tax-info">
        <label class="new-note__checkbox-label">
          <input
            type="checkbox"
            [checked]="taxIncluded()"
            disabled
            class="new-note__checkbox"
          />
          {{ 'global.notes.articles-include-tax' | translate }}
        </label>
      </div>

      <!-- Warehouse -->
      <div class="new-note__detail-group">
        <div class="new-note__detail-label">
          {{ 'global.notes.warehouse' | translate }}
        </div>
        <div class="new-note__detail-value">{{ warehouseName() }}</div>
      </div>

      <!-- Article -->
      <div class="new-note__detail-group">
        <div class="new-note__detail-label">
          {{ 'global.notes.article' | translate }}
        </div>
        @if (showArticleSearch()) {
          <div class="new-note__article-search">
            <v-one-input
              [placeholder]="'global.notes.search-article' | translate"
              type="autocomplete"
              [formControl]="articleSearchControl"
              [autocompleteOptions]="articleAutocompleteOptions()"
              [displayWith]="displayArticleName"
              (autocompleteSelected)="onArticleSelected($event)"
            />
          </div>
        } @else {
          <div class="new-note__article">
            <div class="new-note__article-name">
              {{ articleName() }}
              @if (currentArticleId(); as articleId) {
                <v-one-inventory-article-warning
                  [articleId]="articleId"
                  [articleCode]="currentArticle()?.codigo ?? ''"
                />
              }
            </div>
            <div class="new-note__article-details">
              <span
                >{{ 'global.notes.unit-price' | translate }}
                {{ formatCurrency(articleUnitPrice()) }}</span
              >
              <span class="new-note__article-separator">|</span>
              <span
                >{{ 'global.notes.quantity' | translate }} X{{
                  articleQuantity()
                }}</span
              >
            </div>
          </div>
        }
      </div>

      <!-- Quantity (mobile) -->
      @if (mobileQuantityCtrl; as qtyCtrl) {
        <div class="new-note__detail-label">
          {{ 'global.notes.quantity' | translate }}
        </div>
        <v-one-input
          [type]="'number'"
          class="new-note__detail-input"
          [formControl]="qtyCtrl"
          [max]="
            selectedArticleType() === NOTE_ARTICLE_TYPE.BY_TOTAL
              ? 1
              : maxAdjustQuantity()
          "
          [placeholder]="'0'"
        />
      }

      <!-- Price (mobile) -->
      @if (mobilePriceCtrl; as priceCtrl) {
        <div class="new-note__detail-label">
          {{ 'global.notes.unit-price' | translate }}
        </div>
        <div class="new-note__price-wrapper">
          <v-one-input
            [type]="'number'"
            class="new-note__detail-input new-note__detail-input--with-toggle"
            [formControl]="priceCtrl"
            [max]="
              getCurrentPriceModeForPage() === 'percentage'
                ? 100
                : maxAdjustPrice()
            "
            [placeholder]="'0'"
          />
          <v-one-button
            type="button"
            class="new-note__price-toggle"
            (buttonClick)="togglePriceModeForCurrentPage()"
            [title]="
              getCurrentPriceModeForPage() === 'percentage'
                ? ('global.notes.change-to-price' | translate)
                : ('global.notes.change-to-percentage' | translate)
            "
            [text]="getCurrentPriceModeForPage() === 'percentage' ? '%' : '$'"
          />
        </div>
      }

      <!-- Subtotal -->
      <div class="new-note__detail-group">
        <div class="new-note__detail-label">
          {{ 'global.notes.note-subtotal' | translate }}
        </div>
        <div class="new-note__detail-value">
          {{ formatCurrency(itemSubtotal()) }}
        </div>
      </div>

      <!-- Total with IVA -->
      <div class="new-note__detail-group">
        <div class="new-note__detail-label">
          {{ 'global.notes.note-total-with-tax' | translate }}
        </div>
        <div class="new-note__total">
          {{ formatCurrency(itemTotalWithTax()) }}
          <span class="new-note__tax">
            ({{ 'global.notes.tax-label' | translate }}
            {{ currentArticle()?.tax?.[0]?.value || 0 }}%)
          </span>
        </div>
      </div>

      <!-- Pagination -->
      @if (selectedArticleType() === NOTE_ARTICLE_TYPE.BY_ARTICLE) {
        <div class="new-note__pagination">
          <v-one-paginator
            [totalItems]="totalItems()"
            [currentPage]="currentPage() + 1"
            [pageSizeOptions]="[1]"
            [itemsPerPage]="1"
            [pageIndexBase]="0"
            [variant]="'basic'"
            (pageChange)="onPageChange($event)"
          />
        </div>
      }
    </div>

    <!-- DESKTOP -->
    <div class="new-note__table-wrapper new-note__table-wrapper--desktop">
      <!-- IVA Checkbox -->
      <div class="new-note__tax-info">
        <label class="new-note__checkbox-label">
          <input
            type="checkbox"
            [checked]="taxIncluded()"
            disabled
            class="new-note__checkbox"
          />
          {{ 'global.notes.articles-include-tax' | translate }}
        </label>
      </div>

      <div class="new-note__table-scroll">
        <table class="new-note__table">
          <thead>
            <tr>
              <th>{{ 'global.notes.warehouse' | translate }}</th>
              <th>{{ 'global.notes.article' | translate }}</th>
              <th>{{ 'global.notes.quantity-adjustment' | translate }}</th>
              <th [colSpan]="2">{{ 'global.notes.unit-price' | translate }}</th>
              <th>{{ 'global.notes.note-subtotal' | translate }}</th>
              <th>{{ 'global.notes.note-total-with-tax' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (
              group of itemsForm.controls;
              track group.controls.article.value?.codigo || $index;
              let i = $index
            ) {
              <tr>
                <!-- Warehouse -->
                <td class="new-note__table-warehouse">
                  {{ group.controls.warehouse.value }}
                </td>

                <!-- Article -->
                <td class="new-note__table-article">
                  @if (selectedArticleType() === NOTE_ARTICLE_TYPE.BY_TOTAL) {
                    <v-one-input
                      [placeholder]="'global.notes.search-article' | translate"
                      type="autocomplete"
                      [formControl]="articleSearchControl"
                      [autocompleteOptions]="articleAutocompleteOptions()"
                      [displayWith]="displayArticleName"
                      (autocompleteSelected)="onArticleSelected($event)"
                    />
                  } @else {
                    <div class="new-note__table-article-name">
                      {{
                        group.controls.article.value?.descripcion ||
                          ('global.notes.no-description' | translate)
                      }}
                      @if (
                        resolveArticleId(group.controls.article.value);
                        as articleId
                      ) {
                        <v-one-inventory-article-warning
                          [articleId]="articleId"
                          [articleCode]="
                            group.controls.article.value?.codigo ?? ''
                          "
                        />
                      }
                    </div>
                    <div class="new-note__table-article-details">
                      {{ 'global.notes.unit-price' | translate }}
                      {{ formatCurrency(sales()[i].precio || 0) }}
                      | {{ 'global.notes.quantity' | translate }} x{{
                        sales()[i].cantidad || 0
                      }}
                    </div>
                  }
                </td>

                <!-- Quantity -->
                <td class="new-note__table-input-cell">
                  <v-one-input
                    [type]="'number'"
                    [formControl]="group.controls.quantity"
                    [max]="
                      selectedArticleType() === NOTE_ARTICLE_TYPE.BY_TOTAL
                        ? 1
                        : sales()[i].cantidad || 0
                    "
                    [placeholder]="'0'"
                  />
                </td>

                <!-- Price -->
                <td class="new-note__table-input-cell">
                  <v-one-input
                    [type]="'number'"
                    [formControl]="group.controls.price"
                    [max]="
                      getPriceMode(i) === 'percentage'
                        ? 100
                        : sales()[i].precio || 0
                    "
                    [placeholder]="'0'"
                  />
                </td>

                <td>
                  <v-one-button
                    type="button"
                    class="new-note__price-toggle--desktop"
                    (buttonClick)="togglePriceMode(i)"
                    [title]="
                      getPriceMode(i) === 'percentage'
                        ? ('global.notes.change-to-price' | translate)
                        : ('global.notes.change-to-percentage' | translate)
                    "
                    [text]="
                      getPriceMode(i) === 'percentage' ? '  %  ' : '  $  '
                    "
                  />
                </td>

                <!-- Subtotal -->
                <td class="new-note__table-value">
                  {{ formatCurrency(group.controls.subtotal.value) }}
                </td>

                <!-- Total with IVA -->
                <td class="new-note__table-value">
                  <div class="new-note__table-total">
                    {{ formatCurrency(group.controls.totalWithTax.value) }}
                    <span class="new-note__table-tax">
                      ({{ 'global.notes.tax-label' | translate }}
                      {{ group.controls.article.value?.tax?.[0]?.value || 0 }}%)
                    </span>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <!-- Actions -->
    <div class="new-note__actions-section">
      <div class="new-note__total-display">
        <span class="new-note__total-label"
          >{{ 'global.notes.total-note' | translate }}:</span
        >
        <span class="new-note__total-value">
          {{ formatCurrency(totalWithTax()) }}
        </span>
      </div>

      <div class="new-note__buttons">
        <v-one-button
          class="new-note__button"
          [text]="'global.notes.cancel' | translate"
          [variant]="'outline'"
          (buttonClick)="onCancel()"
        />
        <v-one-button
          class="new-note__button"
          [text]="'global.notes.create-note' | translate"
          [variant]="'primary'"
          [disabled]="
            !hasAnyAdjustment() || conceptControl.invalid || typeControl.invalid
          "
          (buttonClick)="onRegister()"
        />
      </div>
    </div>
  </form>
</div>
